/**
 * Huaca line-icon set. One optimized inline SVG per glyph, matching the house convention already used
 * by HuacaMark / the ThemeToggle sun-moon: `viewBox 0 0 24 24`, `fill:none`, `stroke:currentColor`,
 * `stroke-width 2`, round caps/joins, `aria-hidden` + `focusable=false` (the accessible name lives on
 * the wrapping control). Colour is inherited via `currentColor`, so an icon re-themes for free.
 *
 * Sizing + RTL mirroring come from the `.huaca-icon` classes in components.css: 20px default, with
 * `--sm`/`--lg`/`--xl` modifiers. Direction-bearing icons (chevron-end, back arrow, external-link,
 * log-out) carry `huaca-icon--dir` themselves so a single `[dir='rtl']` rule flips them — no `:has`,
 * no per-icon variants. Never-mirror icons (everything else, incl. sun/moon/user/mail/lock…) omit it.
 */

import type { ReactNode } from 'react';

interface IconProps {
  /** Extra classes, e.g. a size modifier `huaca-icon--sm`. */
  className?: string;
}

function cls(base: string, extra?: string): string {
  return extra ? `${base} ${extra}` : base;
}

/** Shared SVG shell — every icon renders through this so the attributes stay identical. */
function Icon({ className, dir, children }: IconProps & { dir?: boolean; children: ReactNode }) {
  return (
    <svg
      className={cls(dir ? 'huaca-icon huaca-icon--dir' : 'huaca-icon', className)}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      {children}
    </svg>
  );
}

// ── Navigation / chrome ─────────────────────────────────────────────────────────────────────────
export const MenuIcon = ({ className }: IconProps) => (
  <Icon className={className}><path d="M3 6h18M3 12h18M3 18h18" /></Icon>
);
export const ChevronDownIcon = ({ className }: IconProps) => (
  <Icon className={className}><path d="M6 9l6 6 6-6" /></Icon>
);
export const ChevronEndIcon = ({ className }: IconProps) => (
  <Icon className={className} dir><path d="M9 6l6 6-6 6" /></Icon>
);
/* The mirror of ChevronEnd, and it earns a glyph of its own rather than a `scaleX(-1)` at the call
   site: `--dir` ALREADY spends that transform on RTL, so a second flip would cancel it and the
   arrow would point the wrong way in exactly the direction this product ships. Pairs with
   ChevronEnd on the rail's collapse control, where the two of them are the open/closed state. */
export const ChevronStartIcon = ({ className }: IconProps) => (
  <Icon className={className} dir><path d="M15 6l-6 6 6 6" /></Icon>
);
export const ArrowBackIcon = ({ className }: IconProps) => (
  <Icon className={className} dir><path d="M19 12H5M12 19l-7-7 7-7" /></Icon>
);
export const ExternalLinkIcon = ({ className }: IconProps) => (
  <Icon className={className} dir>
    <path d="M15 3h6v6M10 14 21 3M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
  </Icon>
);
export const MoreVerticalIcon = ({ className }: IconProps) => (
  <Icon className={className}>
    <circle cx="12" cy="5" r="1.4" fill="currentColor" stroke="none" />
    <circle cx="12" cy="12" r="1.4" fill="currentColor" stroke="none" />
    <circle cx="12" cy="19" r="1.4" fill="currentColor" stroke="none" />
  </Icon>
);

/* A stack of rows. The set had no LIST glyph at all, which is a real gap for a design system whose
   products are mostly lists — and MenuIcon is the same three lines, so reusing it would put one
   glyph on two different meanings the moment a rail carries both. */
export const ListIcon = ({ className }: IconProps) => (
  <Icon className={className}>
    <path d="M8 6h13M8 12h13M8 18h13" />
    <circle cx="3.5" cy="6" r="1.2" fill="currentColor" stroke="none" />
    <circle cx="3.5" cy="12" r="1.2" fill="currentColor" stroke="none" />
    <circle cx="3.5" cy="18" r="1.2" fill="currentColor" stroke="none" />
  </Icon>
);
/* NOT `dir`. A bookmark's notch points DOWN, not along the writing axis; mirroring it would move
   the ribbon to the other side of a symmetric shape for no reason. */
export const BookmarkIcon = ({ className }: IconProps) => (
  <Icon className={className}><path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z" /></Icon>
);
export const BellIcon = ({ className }: IconProps) => (
  <Icon className={className}>
    <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
    <path d="M13.73 21a2 2 0 0 1-3.46 0" />
  </Icon>
);

// ── Actions ─────────────────────────────────────────────────────────────────────────────────────
export const PlusIcon = ({ className }: IconProps) => (
  <Icon className={className}><path d="M12 5v14M5 12h14" /></Icon>
);
export const XIcon = ({ className }: IconProps) => (
  <Icon className={className}><path d="M18 6 6 18M6 6l12 12" /></Icon>
);
export const CheckIcon = ({ className }: IconProps) => (
  <Icon className={className}><path d="M20 6 9 17l-5-5" /></Icon>
);
export const PencilIcon = ({ className }: IconProps) => (
  <Icon className={className}><path d="M12 20h9M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4 12.5-12.5z" /></Icon>
);
export const TrashIcon = ({ className }: IconProps) => (
  <Icon className={className}>
    <path d="M3 6h18M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2m3 0v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6" />
    <path d="M10 11v6M14 11v6" />
  </Icon>
);
export const CopyIcon = ({ className }: IconProps) => (
  <Icon className={className}>
    <rect x="9" y="9" width="13" height="13" rx="2" />
    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
  </Icon>
);
export const LinkIcon = ({ className }: IconProps) => (
  <Icon className={className}>
    <path d="M10 13a5 5 0 0 0 7.07 0l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
    <path d="M14 11a5 5 0 0 0-7.07 0l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
  </Icon>
);
export const RefreshIcon = ({ className }: IconProps) => (
  <Icon className={className}>
    <path d="M23 4v6h-6M1 20v-6h6" />
    <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
  </Icon>
);
export const LogOutIcon = ({ className }: IconProps) => (
  <Icon className={className} dir>
    <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4M16 17l5-5-5-5M21 12H9" />
  </Icon>
);
/* NOT `dir`. The door-and-arrow above points somewhere and must mirror; a power ring
   points nowhere, and flipping it would be a no-op that lies about the glyph. */
export const PowerIcon = ({ className }: IconProps) => (
  <Icon className={className}>
    <path d="M12 4v8" />
    <path d="M7.05 7.05a7 7 0 1 0 9.9 0" />
  </Icon>
);

// ── Entities ────────────────────────────────────────────────────────────────────────────────────
export const UserIcon = ({ className }: IconProps) => (
  <Icon className={className}>
    <circle cx="12" cy="8" r="3.5" />
    <path d="M5.5 20v-.5a5 5 0 0 1 5-5h3a5 5 0 0 1 5 5v.5" />
  </Icon>
);
export const UserPlusIcon = ({ className }: IconProps) => (
  <Icon className={className}>
    <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
    <circle cx="9" cy="7" r="4" />
    <path d="M19 8v6M22 11h-6" />
  </Icon>
);
export const UserCheckIcon = ({ className }: IconProps) => (
  <Icon className={className}>
    <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
    <circle cx="9" cy="7" r="4" />
    <path d="m16 11 2 2 4-4" />
  </Icon>
);
export const BuildingIcon = ({ className }: IconProps) => (
  <Icon className={className}>
    <path d="M13 21V5a1 1 0 0 1 1-1h5a1 1 0 0 1 1 1v16" />
    <path d="M5 21v-9a1 1 0 0 1 1-1h7" />
    <path d="M15.5 8h2M15.5 12h2M8 16h2" />
    <path d="M3 21h18" />
  </Icon>
);
export const MailIcon = ({ className }: IconProps) => (
  <Icon className={className}><rect x="2" y="4" width="20" height="16" rx="2" /><path d="m22 7-10 6L2 7" /></Icon>
);
export const LockIcon = ({ className }: IconProps) => (
  <Icon className={className}><rect x="3" y="11" width="18" height="11" rx="2" /><path d="M7 11V7a5 5 0 0 1 10 0v4" /></Icon>
);
export const PackageIcon = ({ className }: IconProps) => (
  <Icon className={className}>
    <path d="M16.5 9.4 7.5 4.21M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" />
    <path d="M3.27 6.96 12 12.01l8.73-5.05M12 22.08V12" />
  </Icon>
);

// ── Status / states ─────────────────────────────────────────────────────────────────────────────
export const GaugeIcon = ({ className }: IconProps) => (
  <Icon className={className}><path d="m12 14 4-4M3.34 19a10 10 0 1 1 17.32 0" /></Icon>
);
export const ClockIcon = ({ className }: IconProps) => (
  <Icon className={className}><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" /></Icon>
);
export const AlertTriangleIcon = ({ className }: IconProps) => (
  <Icon className={className}>
    <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
    <path d="M12 9v4M12 17h.01" />
  </Icon>
);
export const CheckCircleIcon = ({ className }: IconProps) => (
  <Icon className={className}><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14M22 4 12 14.01l-3-3" /></Icon>
);
export const InfoCircleIcon = ({ className }: IconProps) => (
  <Icon className={className}><circle cx="12" cy="12" r="10" /><path d="M12 16v-4M12 8h.01" /></Icon>
);

// ── Theme (identical to ThemeToggle's own, kept here so the set is complete) ──────────────────────
export const SunIcon = ({ className }: IconProps) => (
  <Icon className={className}>
    <circle cx="12" cy="12" r="4" />
    <path d="M12 2v2M12 20v2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M2 12h2M20 12h2M6.3 17.7l-1.4 1.4M19.1 4.9l-1.4 1.4" />
  </Icon>
);
export const MoonIcon = ({ className }: IconProps) => (
  <Icon className={className}><path d="M21 12.8A9 9 0 1 1 11.2 3a7 7 0 0 0 9.8 9.8z" /></Icon>
);
